import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useCopilot } from '../contexts/CopilotContext';
import { Box, Button, Typography, CircularProgress, Tooltip, Dialog, DialogTitle, DialogContent, IconButton, useTheme } from '@mui/material';
import {
  Upload as UploadIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useNavigate, useOutletContext, useLocation } from 'react-router-dom';
import NewCertificatesDashboard from '../components/NewCertificatesDashboard';
import BulkUploadSummaryDialog from '../components/BulkUploadSummaryDialog';
import { useAuth } from '../contexts/AuthContext';
import { useUpload } from '../contexts/UploadContext';
import config from '../config';
import type { LayoutContext } from '../App';

interface CertStats {
  total: number;
  expired: number;
  expiringSoon: number;
  propertyCount: number;
}

interface UpcomingReminder {
  id: string;
  reminder_date: string;
  certificate_type?: string;
  property_address?: string;
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    pendingUploadCertificates,
    setPendingUploadCertificates,
    uploadDialogOpen,
    setUploadDialogOpen,
    uploadMatchCount,
    setUploadMatchCount,
    hasVisitedRenewals,
    setHasVisitedRenewals,
    refreshCopilotData,
    filePickerTrigger,
  } = useOutletContext<LayoutContext>();
  const { token: authToken, user, subscription, refreshSubscriptionStatus } = useAuth();
  const { palette } = useTheme();
  const [newlyAddedCertIds, setNewlyAddedCertIds] = useState<string[]>([]);
  const [autoExpandCertIds, setAutoExpandCertIds] = useState<string[]>([]);
  const [collapseAllTrigger, setCollapseAllTrigger] = useState(0);
  const [expiredCertIds, setExpiredCertIds] = useState<string[]>([]);
  const [expiringSoonCertIds, setExpiringSoonCertIds] = useState<string[]>([]);
  const [dashboardRefreshTrigger, setDashboardRefreshTrigger] = useState(0);
  const [certFetchTrigger, setCertFetchTrigger] = useState(0);
  const [stats, setStats] = useState<CertStats | null>(() => {
    try {
      const cached = sessionStorage.getItem('dashboard_stats');
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [upcomingReminders, setUpcomingReminders] = useState<UpcomingReminder[]>(() => {
    try {
      const cached = sessionStorage.getItem('dashboard_reminders');
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });

  const { showMessage } = useCopilot();
  const onboardingShownRef = useRef(false);

  const hour = new Date().getHours();
  const timeOfDay = hour < 12 ? 'morning' : hour < 17 ? 'afternoon' : 'evening';
  const firstName = (user?.name?.split(' ')[0] || '').replace(/^./, c => c.toUpperCase());
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Welcome message + agentic mode auto-enable when arriving from onboarding
  useEffect(() => {
    if (onboardingShownRef.current) return;
    if (!location.state?.fromOnboarding) return;
    onboardingShownRef.current = true;
    navigate('/dashboard', { replace: true, state: {} });

    const googleConnected = location.state?.googleConnected;
    const uploadedCert = location.state?.uploadedCert;
    const certType = uploadedCert?.certificate_type_category || uploadedCert?.certificate_type;

    const msg = googleConnected
      ? `Welcome to your cockpit${firstName ? `, ${firstName}` : ''}! I'm watching your Gmail for new certificates${certType ? ` and I've set a reminder for your ${certType}` : ''}. Keep adding properties using the upload button, or add more providers in Integrations.`
      : `Welcome to your cockpit${firstName ? `, ${firstName}` : ''}! Keep adding certificates using the upload button. Head to Integrations to connect Gmail and add providers so I can work automatically.`;

    showMessage({
      text: msg,
      action: { label: googleConnected ? 'Add providers' : 'Go to Integrations', onClick: () => navigate('/profile') },
      laterLabel: 'Got it',
    });

    // Auto-enable agentic mode when Google was connected during onboarding
    if (googleConnected) {
      const token = authToken || localStorage.getItem('auth_token');
      fetch(`${config.apiUrl}/api/users/settings/agentic-mode`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: true }),
      }).catch(() => {});
    }
  }, [location.state]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const certId = params.get('cert');
    if (certId) {
      setAutoExpandCertIds([certId]);
      navigate('/dashboard', { replace: true });
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(() => setAutoExpandCertIds([]), 5500);
    }
  }, [location.search]);

  // Scroll the highlighted certificate into view once it actually exists in the DOM — a
  // SEPARATE effect keyed on autoExpandCertIds, not location.search. Keeping it in the effect
  // above was the bug: navigate(..., {replace:true}) there changes location.search itself,
  // which immediately re-triggers that effect's cleanup and killed the poll before the property
  // card had time to fetch/expand and the row to actually appear.
  useEffect(() => {
    if (autoExpandCertIds.length === 0) return;
    let attempts = 0;
    const poll = setInterval(() => {
      attempts++;
      const el = document.querySelector('[data-cert-highlight="true"]');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        clearInterval(poll);
      } else if (attempts > 20) {
        clearInterval(poll);
      }
    }, 150);
    return () => clearInterval(poll);
  }, [autoExpandCertIds]);

  useEffect(() => {
    const token = authToken || localStorage.getItem('auth_token');
    if (!token) return;

    fetch(`${config.apiUrl}/api/certificates`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.certificates) {
          const certs = data.certificates;
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const in30 = new Date(today);
          in30.setDate(in30.getDate() + 30);

          const expired = certs.filter((c: any) => c.expiry_date && new Date(c.expiry_date) < today).length;
          const expiringSoon = certs.filter((c: any) => {
            if (!c.expiry_date) return false;
            const exp = new Date(c.expiry_date);
            return exp >= today && exp <= in30;
          }).length;
          const isSentinelFlat = (f: string) => {
            const lower = f.toLowerCase().replace(/[{}]/g, '').trim();
            return lower === 'n/a' || lower === 'whole property' || lower === 'flat_number_not_found';
          };
          const addressFlatsMap = new Map<string, Set<string>>();
          certs.forEach((c: any) => {
            const addr = (c.normalized_address || '').trim();
            if (!addr) return;
            let flats: string[] = [];
            if (Array.isArray(c.flat_number)) {
              flats = c.flat_number;
            } else if (typeof c.flat_number === 'string' && c.flat_number) {
              try { flats = JSON.parse(c.flat_number); } catch { flats = [c.flat_number]; }
            }
            const validFlats = flats.filter((f: string) => f && !isSentinelFlat(f));
            if (!addressFlatsMap.has(addr)) addressFlatsMap.set(addr, new Set());
            if (validFlats.length === 0) {
              addressFlatsMap.get(addr)!.add('__whole__');
            } else {
              validFlats.forEach((f: string) => addressFlatsMap.get(addr)!.add(f.trim()));
            }
          });
          let propertyCount = 0;
          const computedPropertyList: Array<{address: string, flats: string[]}> = [];
          addressFlatsMap.forEach((flatSet, addr) => {
            const namedFlats = Array.from(flatSet).filter(f => f !== '__whole__');
            if (namedFlats.length > 0) {
              propertyCount += namedFlats.length;
              namedFlats.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
              computedPropertyList.push({ address: addr, flats: namedFlats });
            } else {
              propertyCount += 1;
              computedPropertyList.push({ address: addr, flats: [] });
            }
          });
          setPropertyList(computedPropertyList);

          const computed = { total: certs.length, expired, expiringSoon, propertyCount };
          setStats(computed);
          setExpiredCertIds(certs.filter((c: any) => c.expiry_date && new Date(c.expiry_date) < today).map((c: any) => c.id));
          setExpiringSoonCertIds(certs.filter((c: any) => {
            if (!c.expiry_date) return false;
            const exp = new Date(c.expiry_date);
            return exp >= today && exp <= in30;
          }).map((c: any) => c.id));
          try { sessionStorage.setItem('dashboard_stats', JSON.stringify(computed)); } catch {}
        }
      })
      .catch(() => {});

    fetch(`${config.apiUrl}/api/reminders/active-with-certificates`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.reminders) {
          const today = new Date();
          const upcoming = data.reminders
            .filter((r: any) => r.status === 'active' && r.reminder_date && new Date(r.reminder_date) >= today)
            .sort((a: any, b: any) => new Date(a.reminder_date).getTime() - new Date(b.reminder_date).getTime())
            .slice(0, 2)
            .map((r: any) => ({
              id: r.id,
              reminder_date: r.reminder_date,
              certificate_type: r.certificate?.certificate_type || r.certificate_type,
              property_address: r.certificate?.property_address || r.property_address,
            }));
          setUpcomingReminders(upcoming);
          try { sessionStorage.setItem('dashboard_reminders', JSON.stringify(upcoming)); } catch {}
        }
      })
      .catch(() => {});
  }, [authToken, certFetchTrigger]);

  const handleUploadSuccess = () => {
    setDashboardRefreshTrigger(t => t + 1);
    setCertFetchTrigger(t => t + 1);
    refreshCopilotData();
    refreshSubscriptionStatus();
  };

  const attentionCount = (stats?.expired ?? 0) + (stats?.expiringSoon ?? 0);
  const validCerts = (stats?.total ?? 0) - (stats?.expired ?? 0);

  // Banner is a single card with two zones: a solid header band up top (primary-active coral-
  // brown — deliberately not ink/surface-dark, which read as too dark) carrying the greeting,
  // and a flat cream stats/reminders footer below — hairline dividers, no icon chips, no
  // rgba-glass overlays.
  const heroBg = palette.tokens.primaryActive;
  const heroOnColor = palette.tokens.onPrimary;
  const heroSubtleColor = 'rgba(255,255,255,0.78)';
  // Plain coral (#cc785c) doesn't have enough contrast against the header band's own coral-brown
  // fill — this lightened gold variant does, and ties the highlight to "needs attention" instead
  // of being purely decorative.
  const heroAccentColor = '#e3bd6d';

  const handleFilterClick = (certIds: string[]) => {
    setAutoExpandCertIds([]);
    setTimeout(() => setAutoExpandCertIds(certIds), 50);
  };

  // Click-outside dismiss for the expired/expiring filter — clicking anywhere that isn't one of
  // the highlighted certs (marked with data-cert-highlight, see CertificateListRow) clears it, so
  // the user doesn't have to travel back to the banner's "Clear filter" chip every time.
  useEffect(() => {
    if (autoExpandCertIds.length === 0) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('[data-cert-highlight="true"]')) return;
      setAutoExpandCertIds([]);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [autoExpandCertIds.length]);

  const isEmpty = stats !== null && stats.total === 0;

  // ── Inline upload (top-right card) ─────────────────────────────────────────
  // The upload itself lives in UploadContext so it keeps running when the user leaves this page
  const { uploading, progress: uploadProgress, results: uploadResults, summaryOpen: showUploadSummary, startUpload, cancelUpload, openSummary, closeSummary } = useUpload();
  const inlineUploadStatus = (uploading ? 'uploading' : 'idle') as 'idle' | 'uploading' | 'success' | 'error';
  const inlineUploadMsg = '';
  // Rotates through a few "what's happening" phrases one at a time while a file is being
  // extracted — the actual Gemini extraction call can take several seconds per file, and a
  // static "Reading file X of Y…" label made that wait feel stuck. Purely cosmetic — doesn't
  // reflect real backend phases, just gives the user something to read while they wait.
  const UPLOAD_PHASES = ['Reading your files…', 'Extracting information…', 'Organizing your files…', 'Reasoning…'];
  const [uploadPhaseIndex, setUploadPhaseIndex] = useState(0);
  useEffect(() => {
    if (!uploading) return;
    setUploadPhaseIndex(0);
    const id = setInterval(() => setUploadPhaseIndex(i => (i + 1) % UPLOAD_PHASES.length), 1800);
    return () => clearInterval(id);
  }, [uploading]); // eslint-disable-line react-hooks/exhaustive-deps
  // Mock-dashboard toggle removed (dev-only demo feature) — this stays permanently undefined
  // now, kept only because several JSX branches below still read it defensively.
  const [mockCertificates] = useState<any[] | undefined>(undefined);
  const [propertyList, setPropertyList] = useState<Array<{address: string, flats: string[]}>>([]);
  const [propertyListOpen, setPropertyListOpen] = useState(false);

  // An upload that finished while the user was elsewhere is waiting for review
  useEffect(() => {
    if (uploadResults && !uploading && !showUploadSummary) openSummary();
  }, [uploadResults, uploading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleInlineFiles = useCallback((files: File[]) => { startUpload(files); }, [startUpload]);

  const { getRootProps: getUploadRootProps, getInputProps: getUploadInputProps, isDragActive, open: openFileBrowser } = useDropzone({
    onDrop: handleInlineFiles,
    accept: { 'application/pdf': ['.pdf'] },
    multiple: true,
    disabled: inlineUploadStatus === 'uploading',
  });

  const lastFilePickerTriggerRef = useRef(filePickerTrigger);
  useEffect(() => {
    if (filePickerTrigger > lastFilePickerTriggerRef.current) {
      lastFilePickerTriggerRef.current = filePickerTrigger;
      openFileBrowser();
    }
  }, [filePickerTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ flex: 1, p: { xs: 1, sm: 2, md: 3 }, width: '100%', maxWidth: '100%', height: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>

      {/* ── TOP ROW: Banner (3/4) + Upload card (1/4) ── */}
      <Box sx={{ display: { xs: 'block', md: 'flex' }, gap: 2, mb: 3, alignItems: 'stretch' }}>
      <Box sx={{
        flex: 3,
        borderRadius: '14px',
        mb: { xs: 2, md: 0 },
        overflow: 'hidden',
        border: `1px solid ${palette.tokens.hairline}`,
        boxShadow: '0 1px 3px rgba(20,20,19,0.06)',
      }}>
        {/* Hero zone — solid coral ("light") or solid dark-navy ("dark"), per DESIGN.md's
            callout-card-coral / product-mockup-card-dark components. Sized ~10% down from the
            original (a 20% cut felt too small on a second look) — same ratio applied to the
            Properties page's own hero, which mirrors this one. */}
        <Box sx={{ bgcolor: heroBg, p: { xs: 2.7, md: 3.6 } }}>
          {/* Date + status pill */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.8, flexWrap: 'wrap' }}>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.18)', borderRadius: 10, px: 1.35, py: 0.36 }}>
              <Typography sx={{ fontSize: '0.65rem', color: heroOnColor, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                {new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}
              </Typography>
            </Box>
            {attentionCount === 0 && stats && stats.total === 0 && (
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 10, px: 1.35, py: 0.36 }}>
                <Typography sx={{ fontSize: '0.65rem', color: heroOnColor, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                  Let's get started
                </Typography>
              </Box>
            )}
            {attentionCount === 0 && stats && stats.total > 0 && (
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 10, px: 1.35, py: 0.36 }}>
                <Typography sx={{ fontSize: '0.65rem', color: heroOnColor, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase' }}>
                  All clear
                </Typography>
              </Box>
            )}
          </Box>

          {/* Greeting — this is the Dashboard's de facto page title (no h1/h2/h3 exists
              elsewhere on this page), so it renders as a real h2. `variant="displaySm"` still
              controls the visual style (serif, size below) — `component` overrides only the
              semantic tag, decoupling "looks like" from "is structurally." */}
          {/* fontWeight override: the theme's displaySm defaults to 500 (Cormorant Garamond reads
              too thin at 400 for most uses), but this hero headline deliberately drops to the
              literal DESIGN.md rule — never bold the serif display — while the stat numbers below
              keep the theme's 500 default, since a lone digit needs the extra weight to hold up
              at that size. */}
          <Typography variant="displaySm" component="h2" sx={{ fontSize: { xs: '1.8rem', md: '2.52rem' }, fontWeight: 400, color: heroOnColor, lineHeight: 1.2, mb: 1.35, fontVariantNumeric: 'lining-nums' }}>
            Good {timeOfDay}{firstName ? `, ${firstName}` : ''}.{' '}
            {stats ? (
              attentionCount > 0 ? (
                <>
                  <Box component="span" sx={{ color: heroAccentColor }}>
                    {attentionCount} {attentionCount === 1 ? 'certificate' : 'certificates'}
                  </Box>{' '}
                  {attentionCount === 1 ? 'needs' : 'need'} attention.
                </>
              ) : stats.total === 0 ? (
                <>Let's build your <Box component="span" sx={{ color: heroAccentColor }}>portfolio.</Box></>
              ) : (
                <>Your portfolio looks <Box component="span" sx={{ color: palette.tokens.success }}>good.</Box></>
              )
            ) : null}
          </Typography>

          {/* Summary line — one plain sentence, dot-separated */}
          {stats && (
            <Typography sx={{ color: heroSubtleColor, fontSize: '0.81rem', lineHeight: 1.6 }}>
              {stats.total === 0
                ? 'Upload your first certificate and we\'ll organise your portfolio automatically.'
                : [
                    `${stats.propertyCount} ${stats.propertyCount === 1 ? 'property' : 'properties'}`,
                    `${stats.total} certificates`,
                    stats.expired > 0 ? `${stats.expired} expired` : null,
                    stats.expiringSoon > 0 ? `${stats.expiringSoon} expiring within 30 days` : null,
                  ].filter(Boolean).join(' · ')
              }
            </Typography>
          )}
        </Box>

        {/* Stats + reminders footer — always the flat cream treatment (hairline dividers, no
            icon chips, no rgba-glass), matching DESIGN.md's stat-row/feature-card components
            regardless of which hero color is selected above. */}
        <Box sx={{ bgcolor: palette.tokens.canvas, p: { xs: 2.7, md: 3.6 } }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: { xs: 2.25, sm: 2.7 } }}>
            {/* Properties */}
            <Box
              onClick={() => propertyList.length > 0 && setPropertyListOpen(true)}
              sx={{ cursor: propertyList.length > 0 ? 'pointer' : 'default' }}
            >
              <Box sx={{ borderTop: `1px solid ${palette.tokens.hairlineSoft}`, pt: '13px', mb: 0.45 }} />
              <Tooltip
                arrow
                disableHoverListener={subscription?.flat_limit == null}
                title={`You are using ${stats?.propertyCount ?? 0} of the ${subscription?.flat_limit} properties (flats) included in your plan. Add more from your profile.`}
              >
                <Typography variant="displaySm" component="div" sx={{ fontSize: { xs: '1.62rem', md: '1.98rem' }, color: palette.tokens.ink, fontWeight: 700, lineHeight: 1, textAlign: 'center', fontVariantNumeric: 'lining-nums' }}>
                  {stats?.propertyCount ?? '—'}
                  {subscription?.flat_limit != null && (
                    <Box component="span" sx={{ fontSize: '0.55em', fontWeight: 600, color: palette.tokens.muted }}>/{subscription.flat_limit}</Box>
                  )}
                </Typography>
              </Tooltip>
              <Typography sx={{ fontSize: '0.74rem', color: palette.tokens.muted, mt: 0.45, textAlign: 'center' }}>
                Properties
              </Typography>
            </Box>

            {/* Valid Certificates */}
            <Box>
              <Box sx={{ borderTop: `1px solid ${palette.tokens.hairlineSoft}`, pt: '13px', mb: 0.45 }} />
              <Typography variant="displaySm" component="div" sx={{ fontSize: { xs: '1.62rem', md: '1.98rem' }, color: '#2f7a44', fontWeight: 700, lineHeight: 1, textAlign: 'center', fontVariantNumeric: 'lining-nums' }}>
                {stats ? validCerts : '—'}
              </Typography>
              <Typography sx={{ fontSize: '0.74rem', color: palette.tokens.muted, mt: 0.45, textAlign: 'center' }}>
                Valid certificates
              </Typography>
            </Box>

            {/* Expired */}
            <Box
              onClick={stats && stats.expired > 0 ? () => handleFilterClick(expiredCertIds) : undefined}
              sx={{ cursor: stats?.expired ? 'pointer' : 'default' }}
            >
              <Box sx={{ borderTop: `1px solid ${palette.tokens.hairlineSoft}`, pt: '13px', mb: 0.45 }} />
              <Typography variant="displaySm" component="div" sx={{ fontSize: { xs: '1.62rem', md: '1.98rem' }, color: stats?.expired ? palette.tokens.error : palette.tokens.ink, fontWeight: 700, lineHeight: 1, textAlign: 'center', fontVariantNumeric: 'lining-nums' }}>
                {stats?.expired ?? '—'}
              </Typography>
              <Typography sx={{ fontSize: '0.74rem', color: palette.tokens.muted, mt: 0.45, textAlign: 'center' }}>
                Expired
              </Typography>
            </Box>

            {/* Expiring Soon */}
            <Box
              onClick={stats && stats.expiringSoon > 0 ? () => handleFilterClick(expiringSoonCertIds) : undefined}
              sx={{ cursor: stats?.expiringSoon ? 'pointer' : 'default' }}
            >
              <Box sx={{ borderTop: `1px solid ${palette.tokens.hairlineSoft}`, pt: '13px', mb: 0.45 }} />
              <Typography variant="displaySm" component="div" sx={{ fontSize: { xs: '1.62rem', md: '1.98rem' }, color: stats?.expiringSoon ? '#a67a1a' : palette.tokens.ink, fontWeight: 700, lineHeight: 1, textAlign: 'center', fontVariantNumeric: 'lining-nums' }}>
                {stats?.expiringSoon ?? '—'}
              </Typography>
              <Typography sx={{ fontSize: '0.74rem', color: palette.tokens.muted, mt: 0.45, textAlign: 'center' }}>
                Expiring soon
              </Typography>
            </Box>
          </Box>

          {/* Upcoming reminders */}
          {upcomingReminders.length > 0 && (
            <Box sx={{ borderTop: `1px solid ${palette.tokens.hairline}`, pt: 1.8, mt: 2.7 }}>
              <Typography sx={{ fontSize: '0.63rem', color: palette.tokens.mutedSoft, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', mb: 1.35 }}>
                Next Reminders
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                {upcomingReminders.map((reminder) => {
                  const daysUntil = Math.ceil((new Date(reminder.reminder_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                  const isUrgent = daysUntil <= 3;
                  return (
                    <Typography key={reminder.id} sx={{ fontSize: '0.79rem', color: palette.tokens.ink }}>
                      <Box component="span" sx={{ fontWeight: 700 }}>{reminder.certificate_type || 'Reminder'}</Box>
                      {reminder.property_address && (
                        <Box component="span" sx={{ color: palette.tokens.muted }}>
                          {' — '}{reminder.property_address.split(',')[0]}
                        </Box>
                      )}
                      <Box component="span" sx={{ color: isUrgent ? palette.tokens.error : '#2f7a44', fontWeight: 700 }}>
                        {' '}in {daysUntil} day{daysUntil !== 1 ? 's' : ''}
                      </Box>
                    </Typography>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Collapse-clear chip (only when filtering) */}
          {autoExpandCertIds.length > 0 && (
            <Box sx={{ mt: 1.8 }}>
              <Box
                onClick={() => { setAutoExpandCertIds([]); setCollapseAllTrigger(t => t + 1); }}
                sx={{ display: 'inline-flex', alignItems: 'center', bgcolor: palette.tokens.white, border: `1px solid ${palette.tokens.hairline}`, borderRadius: 10, px: 1.35, py: 0.54, cursor: 'pointer', transition: 'all 0.15s', '&:hover': { bgcolor: palette.tokens.surfaceSoft } }}
              >
                <Typography sx={{ fontSize: '0.68rem', color: palette.tokens.muted, fontWeight: 600 }}>✕ Clear filter</Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Box>{/* end banner */}

      {/* Upload card — right 1/4 of the top row, always visible */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          {...getUploadRootProps()}
          sx={{
              height: '100%',
              border: isDragActive ? '1.5px solid #6366f1' : inlineUploadStatus === 'success' ? `1.5px solid ${palette.tokens.success}` : inlineUploadStatus === 'error' ? `1.5px solid ${palette.tokens.error}` : '1.5px dashed #c7d2fe',
              borderRadius: 3,
              p: 3,
              cursor: inlineUploadStatus === 'uploading' ? 'default' : 'pointer',
              backgroundColor: isDragActive ? '#eef2ff' : inlineUploadStatus === 'success' ? '#f0fdf4' : inlineUploadStatus === 'error' ? '#fef2f2' : '#fafbff',
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', textAlign: 'center', gap: 1.5,
              transition: 'all 0.15s',
              '&:hover': inlineUploadStatus === 'idle' ? { borderColor: '#6366f1', backgroundColor: '#eef2ff' } : {},
            }}
          >
            <input {...getUploadInputProps()} />
            <Box sx={{
              width: 44, height: 44, borderRadius: '11px',
              backgroundColor: inlineUploadStatus === 'success' ? '#dcfce7' : inlineUploadStatus === 'error' ? '#fee2e2' : '#eef2ff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {inlineUploadStatus === 'uploading' && <CircularProgress size={20} sx={{ color: '#6366f1' }} />}
              {inlineUploadStatus === 'success' && <CheckCircleOutlineIcon sx={{ fontSize: 22, color: palette.tokens.success }} />}
              {inlineUploadStatus === 'error' && <ErrorOutlineIcon sx={{ fontSize: 22, color: palette.tokens.error }} />}
              {inlineUploadStatus === 'idle' && <UploadIcon sx={{ fontSize: 22, color: '#6366f1' }} />}
            </Box>
            <Box sx={{ width: '100%' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '14px', color: inlineUploadStatus === 'success' ? '#15803d' : inlineUploadStatus === 'error' ? palette.tokens.error : palette.tokens.ink, mb: 0.4 }}>
                {inlineUploadStatus === 'uploading'
                  ? UPLOAD_PHASES[uploadPhaseIndex]
                  : inlineUploadStatus === 'idle' ? 'Upload Certificate' : inlineUploadMsg}
              </Typography>
              {inlineUploadStatus === 'uploading' && uploadProgress && uploadProgress.total > 1 && (
                <>
                  <Typography sx={{ fontSize: '12px', color: palette.tokens.mutedSoft, mb: 0.5 }}>
                    File {uploadProgress.current} of {uploadProgress.total}
                  </Typography>
                  <Box sx={{ width: '100%', height: 4, bgcolor: '#e0e7ff', borderRadius: 2, overflow: 'hidden' }}>
                    <Box sx={{
                      height: '100%', borderRadius: 2, bgcolor: '#6366f1',
                      width: `${Math.round((uploadProgress.current / uploadProgress.total) * 100)}%`,
                      transition: 'width 0.3s ease',
                    }} />
                  </Box>
                </>
              )}
              {inlineUploadStatus === 'uploading' && (
                <Button
                  size="small" variant="outlined" color="error"
                  onClick={(e) => { e.stopPropagation(); cancelUpload(); }}
                  sx={{ mt: 1.25, textTransform: 'none', fontWeight: 600 }}
                >
                  Stop
                </Button>
              )}
              {inlineUploadStatus === 'idle' && (
                <Typography sx={{ fontSize: '12px', color: palette.tokens.mutedSoft, lineHeight: 1.4 }}>
                  {isDragActive ? 'Drop to upload' : 'Drop or click to browse'}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>{/* end top row */}

      {/* ── NON-EMPTY STATE or MOCK MODE: Portfolio ── */}
      {(!isEmpty || mockCertificates !== undefined) && (
        <>
          {/* Portfolio section header */}
          <Box sx={{ mb: 3, mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {/* Next section down from the greeting's h2 — h3 keeps the outline unbroken. */}
              <Typography variant="displaySm" component="h3" sx={{ fontSize: '2rem', fontWeight: 700, color: palette.tokens.ink, whiteSpace: 'nowrap' }}>
                Portfolio
              </Typography>
              {mockCertificates !== undefined && (
                <Box sx={{ px: 1.2, py: 0.3, borderRadius: 10, bgcolor: '#eef2ff', border: '1px solid #c7d2fe' }}>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#6366f1', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Mock</Typography>
                </Box>
              )}
              <Box sx={{ flex: 1, height: '1px', bgcolor: palette.tokens.hairline }} />
            </Box>
          </Box>

          {/* Dashboard Content */}
          <NewCertificatesDashboard
            refreshTrigger={dashboardRefreshTrigger}
            onCertificateClick={(certificateId: string) => {
              console.log('Certificate clicked:', certificateId);
            }}
            pendingUploadCertificates={pendingUploadCertificates}
            setPendingUploadCertificates={setPendingUploadCertificates}
            uploadDialogOpen={uploadDialogOpen}
            setUploadDialogOpen={setUploadDialogOpen}
            uploadMatchCount={uploadMatchCount}
            setUploadMatchCount={setUploadMatchCount}
            hasVisitedRenewals={hasVisitedRenewals}
            setHasVisitedRenewals={setHasVisitedRenewals}
            newlyAddedCertIds={newlyAddedCertIds}
            setNewlyAddedCertIds={setNewlyAddedCertIds}
            autoExpandCertIds={autoExpandCertIds}
            collapseAllTrigger={collapseAllTrigger}
            mockCertificates={mockCertificates}
          />
        </>
      )}
      {/* Property list dialog */}
      <Dialog open={propertyListOpen} onClose={() => setPropertyListOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        {/* Dark header */}
        <Box sx={{ background: `linear-gradient(135deg, ${palette.tokens.surfaceDark} 0%, ${palette.tokens.surfaceDarkElevated} 100%)`, px: 3, py: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: palette.tokens.white, lineHeight: 1.2 }}>Your Portfolio</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)', mt: 0.3 }}>
              {propertyList.length} {propertyList.length === 1 ? 'property' : 'properties'} · {propertyList.reduce((n, p) => n + Math.max(p.flats.length, 1), 0)} units
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setPropertyListOpen(false)} sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: palette.tokens.white, bgcolor: 'rgba(255,255,255,0.1)' } }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          {propertyList.map(({ address, flats }, i) => (
            <Box key={i} sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', gap: 2, borderBottom: i < propertyList.length - 1 ? `1px solid ${palette.tokens.surfaceSoft}` : 'none', '&:hover': { bgcolor: '#fafbff' }, transition: 'background 0.12s' }}>
              {/* Icon */}
              <Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: flats.length > 0 ? '#eef2ff' : '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {flats.length > 0
                  ? <BusinessIcon sx={{ fontSize: 18, color: '#6366f1' }} />
                  : <HomeIcon sx={{ fontSize: 18, color: palette.tokens.success }} />
                }
              </Box>
              {/* Text */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: palette.tokens.ink, lineHeight: 1.3 }}>{address}</Typography>
                {flats.length > 0 ? (
                  <Typography sx={{ fontSize: '0.75rem', color: palette.tokens.muted, mt: 0.2 }}>{flats.join(' · ')}</Typography>
                ) : (
                  <Typography sx={{ fontSize: '0.75rem', color: palette.tokens.mutedSoft, mt: 0.2 }}>Whole property</Typography>
                )}
              </Box>
              {/* Unit count chip */}
              <Box sx={{ bgcolor: flats.length > 0 ? '#eef2ff' : '#f0fdf4', borderRadius: 10, px: 1, py: 0.3, flexShrink: 0 }}>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: flats.length > 0 ? '#6366f1' : palette.tokens.success }}>
                  {flats.length > 0 ? `${flats.length} flats` : '1 unit'}
                </Typography>
              </Box>
            </Box>
          ))}
        </DialogContent>
      </Dialog>

      <BulkUploadSummaryDialog
        open={showUploadSummary}
        extractedCertificates={uploadResults?.extractedCerts ?? []}
        failedFiles={uploadResults?.failedFiles ?? []}
        duplicates={uploadResults?.duplicates ?? []}
        semanticDuplicates={uploadResults?.semanticDuplicates ?? []}
        olderThanExisting={uploadResults?.olderThanExisting ?? []}
        matchFoundCount={uploadResults?.matchFoundCount ?? 0}
        matchFoundRenewals={uploadResults?.matchFoundRenewals ?? []}
        hasExistingCertificates={(stats?.total ?? 0) > 0}
        allCertificates={[]}
        token={authToken || localStorage.getItem('auth_token') || ''}
        onClose={closeSummary}
        onComplete={() => { closeSummary(); handleUploadSuccess(); }}
      />
    </Box>
  );
};

export default DashboardPage;
